/**
 * Broker trade confirmations / contract notes (email body text or PDF text lines) → one proposed trade
 * (docs/TRADE-INBOX.md). Never committed here: the user confirms each one in the trade inbox.
 * Table-driven: text is split into cells (lines, then runs of 2+ spaces / tabs / pipes, as pdfjs and HTML-to-text
 * leave columns); each cell is matched against label rules, and the value is the rest of the cell or the next cell.
 * Broker profiles add their own labels in front of the generic ones. Narrative lines ("WE HAVE BOUGHT 1,000 XYZ @ 1.23")
 * are a fallback. Layouts are modelled on public descriptions, not real samples — add real labels to the tables.
 */
import { z } from 'zod';
import { CentsSchema, DecimalStringSchema, ISODateSchema } from '../types.js';
import { Decimal, parseMoney } from '../money.js';
import { parseDate } from '../importers/dates.js';
export const CONTRACT_NOTE_FIELDS = [
    'side', 'code', 'units', 'price', 'considerationCents', 'brokerageCents', 'gstCents', 'totalCents',
    'tradeDate', 'settlementDate', 'confirmationNo',
];
/** What a trade can't be recorded without. */
export const REQUIRED_CONTRACT_NOTE_FIELDS = ['side', 'code', 'units', 'price', 'tradeDate'];
export const ParsedContractNoteSchema = z.object({
    broker: z.string().nullable(),
    side: z.enum(['buy', 'sell']).nullable(),
    /** ASX code, upper case, no `.AX`. */
    code: z.string().regex(/^[A-Z0-9]{3,6}$/).nullable(),
    units: DecimalStringSchema.nullable(),
    /** Per unit in dollars (contract notes quote up to 4+ dp, finer than cents). */
    price: DecimalStringSchema.nullable(),
    considerationCents: CentsSchema.nullable(),
    brokerageCents: CentsSchema.nullable(),
    gstCents: CentsSchema.nullable(),
    /** Net cash: buy = consideration + costs; sell = consideration − costs. */
    totalCents: CentsSchema.nullable(),
    /** The brokerage figure already contains GST (e.g. "Brokerage incl GST"). */
    brokerageIncludesGst: z.boolean(),
    tradeDate: ISODateSchema.nullable(),
    settlementDate: ISODateSchema.nullable(),
    confirmationNo: z.string().max(40).nullable(),
    /** high = read from a labelled field; low = inferred (narrative line, derived, guessed). */
    confidence: z.record(z.string(), z.enum(['high', 'low'])),
    missing: z.array(z.enum(CONTRACT_NOTE_FIELDS)),
    warnings: z.array(z.string()),
});
/** Order matters: the first rule whose label matches a cell wins it. */
const GENERIC_RULES = [
    { field: 'settlementDate', label: /^settle(?:ment|s)?(?: date)?\b/i },
    { field: 'tradeDate', label: /^(?:trade|transaction|contract|execution|as at|order) date\b/i },
    { field: 'tradeDate', label: /^date\b/i },
    { field: 'confirmationNo', label: /^(?:confirmation|contract(?: note)?|trade|reference|ref)\s*(?:no\.?|number|#|id)/i },
    { field: 'brokerageCents', label: /^brokerage\b[^:\d$]*\b(?:inc(?:l|luding)?|incl\.)\.?\s*gst\b/i, incGst: true },
    { field: 'brokerageCents', label: /^brokerage\b(?:\s*(?:&|and)\s*costs?)?(?:\s*\((?:ex|excl?)\.?\s*gst\))?/i },
    { field: 'gstCents', label: /^(?:total\s+)?gst\b/i },
    { field: 'considerationCents', label: /^(?:consideration|gross (?:amount|value|consideration)|trade value|value)\b/i },
    { field: 'totalCents', label: /^(?:total(?: (?:cost|amount|payable|proceeds|due|value))?|net (?:amount|proceeds|value|consideration)|amount (?:payable|due|owing)|settlement (?:amount|value))\b/i },
    { field: 'side', label: /^(?:buy\s*\/\s*sell|transaction(?: type)?|trade type|order type|side|action)\b/i },
    { field: 'code', label: /^(?:asx code|security code|stock code|code|ticker|symbol)\b/i },
    { field: 'code', label: /^(?:security|stock|company|shares in)\b/i },
    { field: 'units', label: /^(?:quantity|units|volume|number of (?:shares|units)|qty)\b/i },
    { field: 'price', label: /^(?:average price|avg\.? price|price(?: per (?:share|unit))?|unit price|executed price)\b/i },
];
/** Broker-specific labels (added in front of the generic ones). Extend from real notes as they arrive. */
export const BROKER_PROFILES = [
    { id: 'nabtrade', detect: /nabtrade/i, rules: [{ field: 'considerationCents', label: /^trade value\b/i }] },
    { id: 'commsec', detect: /commsec|commonwealth securities/i, rules: [
            { field: 'brokerageCents', label: /^brokerage\s*&\s*costs?\s+incl\.?\s*gst\b/i, incGst: true },
            { field: 'tradeDate', label: /^as at date\b/i },
        ] },
];
const NUM = /-?\$?\s?\d[\d,]*(?:\.\d+)?/;
const firstNumber = (s) => {
    const m = NUM.exec(s.replace(/\b[A-Z]{3}\s(?=\$?\d)/, ''));
    return m ? m[0].replace(/[$,\s]/g, '') : null;
};
const toDecimal = (s) => {
    const n = firstNumber(s);
    if (!n)
        return null;
    try {
        const d = new Decimal(n);
        return d.isFinite() && d.gte(0) ? d.toString() : null;
    }
    catch {
        return null;
    }
};
const toCents = (s) => {
    const n = firstNumber(s);
    if (!n)
        return null;
    try {
        return Math.abs(parseMoney(n));
    }
    catch {
        return null;
    }
};
const DATE_FORMATS = ['dd/MM/yyyy', 'd MMM yyyy', 'd MMMM yyyy', 'dd/MM/yy', 'MMM d yyyy'];
const toDate = (s) => {
    const m = /\d{4}-\d{2}-\d{2}|\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}|\d{1,2}\s+[A-Za-z]{3,9}\.?,?\s+\d{4}|[A-Za-z]{3,9}\s+\d{1,2},?\s+\d{4}/.exec(s);
    if (!m)
        return null;
    const v = m[0].replace(/[.,]/g, ' ').replace(/\s+/g, ' ');
    for (const f of DATE_FORMATS) {
        const d = parseDate(v, f);
        if (d)
            return d;
    }
    return null;
};
const toSide = (s) => {
    if (/^\s*b\s*$/i.test(s) || /\b(?:buy|bought|purchase[ds]?)\b/i.test(s))
        return 'buy';
    if (/^\s*s\s*$/i.test(s) || /\b(?:sell|sold|sale)\b/i.test(s))
        return 'sell';
    return null;
};
const isCode = (s) => /^[A-Z0-9]{3,6}$/.test(s) && /[A-Z]/.test(s);
const toCode = (s, loose) => {
    const paren = /\(([A-Z0-9]{3,6})(?:\.AX)?\)/.exec(s)?.[1];
    if (paren && isCode(paren))
        return paren;
    const head = /^\s*([A-Z0-9]{3,6})(?:\.AX)?(?=$|\s+-|\s*$|\s{2,}|\s+[A-Z])/.exec(s)?.[1];
    if (head && isCode(head) && (loose || /^\s*[A-Z0-9]{3,6}(?:\.AX)?\s*(?:$|-)/.test(s)))
        return head;
    return null;
};
const toRef = (s) => {
    const m = /[A-Z0-9][A-Z0-9-]{3,39}/i.exec(s.replace(/^[\s:#.-]+/, ''));
    return m && /\d/.test(m[0]) ? m[0].toUpperCase() : null;
};
const readers = {
    side: (s) => toSide(s), code: (s, r) => toCode(s, !/security|stock|company|shares/i.test(r.label.source)),
    units: toDecimal, price: toDecimal, considerationCents: toCents, brokerageCents: toCents, gstCents: toCents, totalCents: toCents,
    tradeDate: toDate, settlementDate: toDate, confirmationNo: toRef,
};
/** "Quantity: 1,000 Price: 1.2345" on one line → a cell per label (a split only where a known label follows a value). */
/** Cells longer than this are prose, not "Label: value" pairs; skipping them keeps the scan linear on hostile input. */
const MAX_INLINE_CELL = 500;
const splitInline = (cell, rules) => {
    if (cell.length > MAX_INLINE_CELL)
        return [cell];
    const out = [];
    let start = 0;
    for (const m of cell.matchAll(/\s+(?=[A-Za-z])/g)) {
        const at = m.index + m[0].length;
        if (/:\s*\S/.test(cell.slice(start, at)) && rules.some((r) => r.label.test(cell.slice(at)))) {
            out.push(cell.slice(start, m.index).trim());
            start = at;
        }
    }
    out.push(cell.slice(start).trim());
    return out;
};
/** Untrusted input is capped; a contract note is a page or two. */
const MAX_TEXT = 200_000;
export const parseContractNote = (text) => {
    const clean = text.slice(0, MAX_TEXT).replace(/\r\n?/g, '\n').replace(/^[ \t]*(?:>[ \t]?)+/gm, '').replace(/ /g, ' ');
    const profile = BROKER_PROFILES.find((p) => p.detect.test(clean)) ?? null;
    const rules = [...(profile?.rules ?? []), ...GENERIC_RULES];
    const cells = clean.split('\n').flatMap((l) => l.split(/\t|\s*\|\s*|\s{2,}/)).flatMap((c) => splitInline(c.trim(), rules)).filter(Boolean);
    const found = {};
    const confidence = {};
    let brokerageIncludesGst = false;
    const set = (k, v, c) => {
        if (v === null || found[k] !== undefined)
            return;
        found[k] = v;
        confidence[k] = c;
    };
    cells.forEach((cell, i) => {
        const rule = rules.find((r) => r.label.test(cell));
        if (!rule || found[rule.field] !== undefined)
            return;
        const rest = cell.replace(rule.label, '').replace(/^\s*(?:\([^)]*\))?\s*[:=-]?\s*/, '');
        const read = readers[rule.field];
        const next = cells[i + 1];
        const value = (rest ? read(rest, rule) : null) ?? (next !== undefined && !rules.some((r) => r.label.test(next)) ? read(next, rule) : null);
        if (value === null)
            return;
        set(rule.field, value, 'high');
        if (rule.field === 'brokerageCents' && (rule.incGst || /inc(?:l|luding)?\.?\s*gst/i.test(cell)))
            brokerageIncludesGst = true;
    });
    // Narrative fallbacks: "WE HAVE BOUGHT 1,000 XYZ @ 1.2345", "Sold 500 units of ABC at $4.56".
    const headline = /\bwe have (bought|sold)\b|\b(buy|sell|purchase|sale) (?:confirmation|contract note)\b|\bconfirmation of (purchase|sale)\b/i.exec(clean);
    if (headline)
        set('side', toSide(headline[0]), 'high');
    const narrative = [...clean.matchAll(/\b(bought|sold|buy|sell)\s+(?:for you\s+)?(\d[\d,]*(?:\.\d+)?)\s+(?:(?:units?|shares?)\s+(?:of|in)\s+)?([A-Za-z0-9]{3,6})(?:\.AX)?\b(?:[^\n@]{0,40}?(?:@|\bat)\s*\$?\s*(\d[\d,]*\.\d+))?/gi)]
        .find((m) => isCode(m[3]));
    if (narrative) {
        set('side', toSide(narrative[1]), 'low');
        set('units', toDecimal(narrative[2]), 'low');
        set('code', narrative[3], 'low');
        if (narrative[4])
            set('price', toDecimal(narrative[4]), 'low');
    }
    set('side', toSide(/\b(?:bought|sold|buy|sell)\b/i.exec(clean)?.[0] ?? ''), 'low');
    // Derive what the arithmetic gives.
    if (found.price === undefined && found.considerationCents !== undefined && found.units && !new Decimal(found.units).isZero()) {
        set('price', new Decimal(found.considerationCents).div(100).div(found.units).toDecimalPlaces(6).toString(), 'low');
    }
    if (found.considerationCents === undefined && found.price && found.units) {
        set('considerationCents', new Decimal(found.price).mul(found.units).mul(100).toDecimalPlaces(0).toNumber(), 'low');
    }
    const warnings = [];
    const { considerationCents: cons, brokerageCents: brok, gstCents: gst, totalCents: total, side } = found;
    if (cons !== undefined && brok !== undefined && total !== undefined && side) {
        const costs = brok + (brokerageIncludesGst ? 0 : gst ?? 0);
        const expect = side === 'buy' ? cons + costs : cons - costs;
        // ponytail: notes may add fees we don't read (clearing, application money); a mismatch only warns.
        if (Math.abs(expect - total) > 1)
            warnings.push('The total does not equal consideration ± brokerage and GST — check the figures.');
    }
    if (found.tradeDate && found.settlementDate && found.settlementDate < found.tradeDate)
        warnings.push('Settlement date is before the trade date.');
    const core = ['side', 'code', 'units', 'price'].filter((k) => found[k] !== undefined).length;
    if (core < 2)
        return null;
    return ParsedContractNoteSchema.parse({
        broker: profile?.id ?? null,
        side: found.side ?? null, code: found.code ?? null, units: found.units ?? null, price: found.price ?? null,
        considerationCents: found.considerationCents ?? null, brokerageCents: found.brokerageCents ?? null, gstCents: found.gstCents ?? null,
        totalCents: found.totalCents ?? null, brokerageIncludesGst,
        tradeDate: found.tradeDate ?? null, settlementDate: found.settlementDate ?? null, confirmationNo: found.confirmationNo ?? null,
        confidence, missing: CONTRACT_NOTE_FIELDS.filter((k) => found[k] === undefined), warnings,
    });
};
//# sourceMappingURL=contractNote.js.map